import { useState } from "react";
import { Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "../../lib/api";

export function AiSummary({ r, reload }) {
  const [busy, setBusy] = useState(false);
  const s = r.ai_summary;
  const run = async () => {
    setBusy(true);
    try { await api.post(`/admin/requests/${r.id}/ai/summary`); reload(); toast.success("Résumé Astra mis à jour"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="k-card p-5 bg-[#FBF8F3]" data-testid="ai-summary">
      <div className="flex items-center justify-between gap-3">
        <p className="k-eyebrow flex items-center gap-2 text-[#57534E]"><Sparkles size={13} className="text-[#C25E38]" /> Résumé Astra</p>
        <button className="k-link text-xs flex items-center gap-1" disabled={busy} onClick={run} data-testid="ai-summary-run"><RefreshCw size={12} className={busy ? "animate-spin" : ""} /> {s ? "Actualiser" : "Générer"}</button>
      </div>
      {!s && <p className="text-sm text-[#8C857B] mt-2">{busy ? "Astra lit le dossier…" : "Aucun résumé pour l'instant."}</p>}
      {s && !s.raw && (
        <div className="mt-3 grid gap-2 text-sm">
          <p data-testid="ai-summary-text">{s.summary}</p>
          <dl className="grid sm:grid-cols-3 gap-x-4 gap-y-1 text-xs text-[#57534E]">
            <div><dt className="uppercase tracking-wider text-[10px] text-[#8C857B]">Produits</dt><dd>{s.products}</dd></div>
            <div><dt className="uppercase tracking-wider text-[10px] text-[#8C857B]">Logo</dt><dd>{s.logo}</dd></div>
            <div><dt className="uppercase tracking-wider text-[10px] text-[#8C857B]">Délai</dt><dd>{s.timing}</dd></div>
          </dl>
          {s.watchpoints?.length > 0 && <ul className="flex flex-wrap gap-2 mt-1" data-testid="ai-summary-watchpoints">{s.watchpoints.map((w) => <li key={w} className="k-badge text-[#A84C2A] border-[#E5A992]">{w}</li>)}</ul>}
          {s.next_step && <p className="text-xs mt-1"><strong>Suggestion :</strong> {s.next_step}</p>}
          <p className="text-[10px] text-[#8C857B]">Généré par IA le {fmtDateTime(s.at)} — à vérifier, ne remplace pas la lecture du dossier.</p>
        </div>
      )}
      {s?.raw && <p className="text-sm whitespace-pre-line mt-2">{s.raw}</p>}
    </section>
  );
}

const LABELS = { reply: "Réponse Astra", reminder: "Relance Astra", express_quote: "Texte Astra", info_request: "Complément Astra" };

export function AstraDraft({ rid, kind = "reply", onText, className = "" }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [instr, setInstr] = useState("");
  const run = async () => {
    setBusy(true);
    try { const r = await api.post(`/admin/requests/${rid}/ai/draft`, { kind, instructions: instr }); onText(r.data.text); setOpen(false); toast.success("Proposition d'Astra insérée — relisez avant d'envoyer"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <span className={`inline-flex flex-wrap items-center gap-2 ${className}`}>
      <button type="button" className="k-link text-xs flex items-center gap-1" disabled={busy} onClick={() => (open ? run() : setOpen(true))} data-testid={`astra-draft-${kind}`}>
        <Sparkles size={12} className="text-[#C25E38]" /> {busy ? "Astra rédige…" : open ? "Générer" : LABELS[kind] || "Proposer avec Astra"}
      </button>
      {open && !busy && <>
        <input className="k-input py-1 text-xs w-56" placeholder="Consigne (optionnel) : ex. proposer un appel" value={instr} onChange={(e) => setInstr(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} data-testid={`astra-draft-${kind}-instr`} />
        <button type="button" className="text-xs text-[#8C857B]" onClick={() => setOpen(false)}>Annuler</button>
      </>}
    </span>
  );
}

export function LogoAnalysis({ file, packId, widthMm = 80, onResult }) {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(file?.ai_analysis || null);
  const run = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/ai/logo-analysis", { file_id: file.id, pack_id: packId || null, width_mm: widthMm }); setRes(r.data); onResult?.(r.data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const ok = /\.(png|jpe?g|webp)$/i.test(file?.name || "");
  if (!ok) return null;
  return (
    <span className="block w-full" data-testid={`logo-analysis-${file.id}`}>
      <button type="button" className="k-link text-xs flex items-center gap-1" disabled={busy} onClick={run} data-testid={`logo-analysis-run-${file.id}`}><Sparkles size={12} className="text-[#C25E38]" /> {busy ? "Astra analyse…" : res ? "Ré-analyser le logo" : "Analyser le logo (Astra)"}</button>
      {res && !res.raw && (
        <span className="block mt-1 text-xs text-[#57534E]" data-testid={`logo-analysis-result-${file.id}`}>
          {res.description} — <strong>{res.colors} couleur(s)</strong>{res.color_names?.length ? ` (${res.color_names.join(", ")})` : ""}, complexité {res.complexity}, ≈{Number(res.stitches_estimate || 0).toLocaleString("fr-FR")} pts à {widthMm} mm{res.vectorize_needed && ", vectorisation conseillée"}.
          {res.fine_details?.length > 0 && <span className="block">Détails fins : {res.fine_details.join(" · ")}</span>}
          {res.advice && <span className="block italic">{res.advice}</span>}
          {"pack_compatible" in res && <span className={`block font-medium ${res.pack_compatible ? "text-[#2F7D5B]" : "text-[#A84C2A]"}`} data-testid={`logo-analysis-pack-${file.id}`}>{res.pack_compatible ? "Compatible avec le pack" : `Hors limites du pack : ${res.pack_issues.join(" ; ")}`}</span>}
        </span>
      )}
    </span>
  );
}
